import { z } from 'zod'

import { extractSafeValues } from './response-contract'
import {
  ResponseContractError,
  type ResponseContractIssue,
} from './response-contract-error'
import {
  flashResponseContracts,
  operationResponseContracts,
  pageResponseContracts,
  sharedResponsePropsSchema,
  sessionlessResponsePropsSchema,
  type PageResponseName,
} from './responses'

const envelopeSchema = z.object({
  component: z.string().trim().min(1),
  props: z.record(z.string(), z.unknown()),
  url: z.string().trim().min(1),
  version: z.string().nullable(),
}).loose()

const sharedPropNames = new Set(['csrf', 'errors', 'user'])

function issuePath(prefix: string, path: PropertyKey[]): string {
  const suffix = path.map(String).join('.')
  if (!prefix) return suffix
  return suffix ? `${prefix}.${suffix}` : prefix
}

function contractIssues(error: z.ZodError, prefix: string): ResponseContractIssue[] {
  const issues: ResponseContractIssue[] = []
  for (const issue of error.issues) {
    if (issue.code === 'unrecognized_keys') {
      for (const key of issue.keys) {
        issues.push({
          path: issuePath(prefix, [...issue.path, key]),
          code: issue.code,
          message: `Unrecognized key: ${key}`,
        })
      }
      continue
    }

    issues.push({
      path: issuePath(prefix, issue.path),
      code: issue.code,
      message: issue.message,
    })
  }
  return issues
}

function throwContractError(
  contract: string,
  issues: ResponseContractIssue[],
  safeValues: Readonly<Record<string, unknown>> = {},
): never {
  throw new ResponseContractError({ contract, issues, safeValues })
}

function isPageResponseName(component: string): component is PageResponseName {
  return Object.hasOwn(pageResponseContracts, component)
}

export type ValidatedPageResponse = Readonly<{
  page: Record<string, unknown> & {
    component: PageResponseName
    props: Record<string, unknown>
    flash: Record<string, unknown>
    url: string
    version: string | null
  }
  flashFailures: readonly string[]
  contractError?: ResponseContractError
}>

export function validatePageResponse(
  input: unknown,
  options: Readonly<{ operation?: string }> = {},
): ValidatedPageResponse {
  const envelopeResult = envelopeSchema.safeParse(input)
  if (!envelopeResult.success) {
    throwContractError('inertia.envelope', contractIssues(envelopeResult.error, 'response'))
  }

  const page = envelopeResult.data
  const component = page.component
  if (!isPageResponseName(component)) {
    throwContractError('inertia.component', [{
      path: 'component',
      code: 'unregistered_component',
      message: `No response contract is registered for ${component}`,
    }])
  }

  const sharedProps = Object.fromEntries(
    Object.entries(page.props).filter(([key]) => sharedPropNames.has(key)),
  )
  const pageProps = Object.fromEntries(
    Object.entries(page.props).filter(([key]) => !sharedPropNames.has(key)),
  )
  const sharedSchema = ['About', 'WhatToExpect', 'CodeOfConduct', 'Error'].includes(component)
    ? sessionlessResponsePropsSchema
    : sharedResponsePropsSchema
  const sharedResult = sharedSchema.safeParse(sharedProps)
  const pageContract = pageResponseContracts[component]
  const pageResult = pageContract.schema.safeParse(pageProps)
  const issues = [
    ...(sharedResult.success ? [] : contractIssues(sharedResult.error, 'props')),
    ...(pageResult.success ? [] : contractIssues(pageResult.error, 'props')),
  ]

  if (!sharedResult.success || !pageResult.success) {
    throwContractError(
      pageContract.name,
      issues,
      extractSafeValues(pageProps, pageContract.safeValuePaths),
    )
  }

  const flashInput = page.flash === undefined ? {} : page.flash
  if (typeof flashInput !== 'object' || flashInput === null || Array.isArray(flashInput)) {
    return {
      page: { ...page, component, props: { ...sharedResult.data, ...pageResult.data }, flash: {} },
      flashFailures: ['base'],
      contractError: new ResponseContractError({
        contract: 'flash',
        issues: [{ path: 'flash', code: 'invalid_type', message: 'Flash must be an object' }],
      }),
    }
  }

  const safeFlash: Record<string, unknown> = {}
  const flashIssues: ResponseContractIssue[] = []
  const flashFailures: string[] = []
  for (const [key, value] of Object.entries(flashInput)) {
    if (!Object.hasOwn(flashResponseContracts, key)) {
      flashFailures.push(key)
      flashIssues.push({
        path: `flash.${key}`,
        code: 'unrecognized_key',
        message: `No flash response contract is registered for ${key}`,
      })
      continue
    }

    const schema = flashResponseContracts[key as keyof typeof flashResponseContracts]
    const result = schema.safeParse(value)
    if (result.success) {
      safeFlash[key] = result.data
    } else {
      flashFailures.push(key)
      flashIssues.push(...contractIssues(result.error, `flash.${key}`))
    }
  }

  const operation = options.operation
  const operationIssues: ResponseContractIssue[] = []
  if (operation) {
    if (!Object.hasOwn(operationResponseContracts, operation)) {
      throwContractError('operation', [{
        path: 'operation',
        code: 'unregistered_operation',
        message: `No response contract is registered for ${operation}`,
      }])
    }

    const contracts = operationResponseContracts[operation as keyof typeof operationResponseContracts]
    if (contracts.kind !== 'page') {
      throwContractError(`operation.${operation}`, [{
        path: 'status',
        code: 'expected_no_content',
        message: `Operation ${operation} must return no content`,
      }])
    }
    const hasErrors = Object.keys(sharedResult.data.errors).length > 0
    const operationResult = hasErrors
      ? contracts.failure.safeParse({ errors: sharedResult.data.errors })
      : contracts.success.safeParse({ flash: flashInput })

    if (!operationResult.success) {
      const operationPrefix = hasErrors ? 'props' : ''
      operationIssues.push(...contractIssues(operationResult.error, operationPrefix))
      if (hasErrors) {
        throwContractError(`operation.${operation}`, operationIssues)
      }

      for (const issue of operationIssues) {
        const match = /^flash\.([^.]+)/.exec(issue.path)
        if (match) {
          delete safeFlash[match[1]]
          if (!flashFailures.includes(match[1])) flashFailures.push(match[1])
        }
      }
    }
  }

  const safePage = {
    ...page,
    component,
    props: { ...sharedResult.data, ...pageResult.data },
    flash: safeFlash,
  }

  return {
    page: safePage,
    flashFailures,
    ...(flashIssues.length > 0 || operationIssues.length > 0
      ? { contractError: new ResponseContractError({
          contract: operationIssues.length > 0 ? `operation.${operation}` : 'flash',
          issues: [...new Map(
            [...flashIssues, ...operationIssues].map((issue) => [
              `${issue.path}:${issue.code}:${issue.message}`,
              issue,
            ]),
          ).values()],
        }) }
      : {}),
  }
}

export function requireRequestedProps(
  props: Readonly<Record<string, unknown>>,
  requestedProps: readonly string[],
): void {
  const missing = requestedProps.filter((prop) => !Object.hasOwn(props, prop))
  if (missing.length === 0) return

  throw new ResponseContractError({
    contract: 'inertia.partial',
    issues: missing.map((prop) => ({
      path: `props.${prop}`,
      code: 'missing_requested_prop',
      message: `Requested prop ${prop} was not returned`,
    })),
  })
}
